import { forwardRef, useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/analytics';
import { Lock, MapPin } from 'lucide-react';
import { getHeroImageUrl, getHeroVideoConfig, HeroVideoConfig } from '@/lib/admin/site-images';
import { usePrefersReducedMotion, useIsMobileWidth } from '@/hooks/useMediaQuery';

interface HeroProps {
  onRegisterClick: () => void;
  onConsultClick: () => void;
  onVIPPreviewClick?: () => void;
  showVIPPreviewCTA?: boolean;
}

const DEFAULT_HERO_IMAGE = '/img/hero-lagoon.jpg';

export const Hero = forwardRef<HTMLElement, HeroProps>(({ onRegisterClick, onConsultClick, onVIPPreviewClick, showVIPPreviewCTA = false }, ref) => {
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [videoConfig, setVideoConfig] = useState<HeroVideoConfig | null>(null);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Accessibility and mobile hooks
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobile = useIsMobileWidth(768);

  useEffect(() => {
    // Load hero media configuration
    Promise.all([
      getHeroImageUrl(),
      getHeroVideoConfig()
    ]).then(([imageUrl, videoConf]) => {
      setVideoConfig(videoConf);
      
      // Determine the appropriate image to use based on media type
      // For image mode: use configured static image
      // For video mode: use poster image
      // Fallback to default only if nothing is configured
      let imageToLoad: string;
      if (videoConf.mediaType === 'video' && videoConf.posterUrl) {
        // Video mode: use poster
        imageToLoad = videoConf.posterUrl;
      } else if (imageUrl) {
        // Image mode: use configured static image
        imageToLoad = imageUrl;
      } else {
        // No configuration: use default
        imageToLoad = DEFAULT_HERO_IMAGE;
      }
      
      // Preload the image
      const img = new Image();
      img.onload = () => {
        // Only set to default if we confirmed there's no configured image
        setHeroImageUrl(imageToLoad);
        setMediaLoaded(true);
        setIsLoading(false);
      };
      img.onerror = () => {
        // If configured image fails, try default as fallback
        if (imageToLoad !== DEFAULT_HERO_IMAGE) {
          // Configured image/poster failed, try default as fallback
          const defaultImg = new Image();
          defaultImg.onload = () => {
            setHeroImageUrl(DEFAULT_HERO_IMAGE);
            setMediaLoaded(true);
            setIsLoading(false);
          };
          defaultImg.onerror = () => {
            // Even default failed, but show it anyway
            setHeroImageUrl(DEFAULT_HERO_IMAGE);
            setMediaLoaded(true);
            setIsLoading(false);
          };
          defaultImg.src = DEFAULT_HERO_IMAGE;
        } else {
          // Already using default and it failed
          setHeroImageUrl(DEFAULT_HERO_IMAGE);
          setMediaLoaded(true);
          setIsLoading(false);
        }
      };
      img.src = imageToLoad;
    }).catch(error => {
      console.error('Error loading hero media config:', error);
      // Only use default on error if we can't determine what should be shown
      setHeroImageUrl(DEFAULT_HERO_IMAGE);
      setMediaLoaded(true);
      setIsLoading(false);
    });
  }, []);

  // Handle video error - fall back to image
  const handleVideoError = () => {
    console.warn('Hero video failed to load, falling back to image');
    setVideoError(true);
  };

  // Determine if we should show video
  const shouldShowVideo = (() => {
    if (!videoConfig) return false;
    if (videoConfig.mediaType !== 'video') return false;
    if (!videoConfig.videoUrlMp4 && !videoConfig.videoUrlWebm) return false;
    if (videoError) return false;
    if (prefersReducedMotion) return false;
    if (isMobile && videoConfig.mobileBehavior === 'static') return false;
    return true;
  })();

  // Get the image to display (poster for video mode, or static image)
  // heroImageUrl is already set correctly by useEffect (configured image > poster > default)
  const displayImageUrl = heroImageUrl || DEFAULT_HERO_IMAGE;

  if (import.meta.env.DEV) {
    console.log('Hero render:', { 
      showVIPPreviewCTA, 
      hasClickHandler: !!onVIPPreviewClick,
      willShowVIPButton: showVIPPreviewCTA,
      heroImageUrl,
      mediaType: videoConfig?.mediaType,
      shouldShowVideo,
      prefersReducedMotion,
      isMobile,
      videoError
    });
  }

  return (
    <section 
      ref={ref} 
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
    >
      {/* Background Media (Video or Image) */}
      {mediaLoaded && displayImageUrl && (
        <div className="absolute inset-0 z-0">
          {shouldShowVideo && videoConfig ? (
            // Video Background
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              poster={displayImageUrl}
              className="w-full h-full object-cover"
              onError={handleVideoError}
            >
              {videoConfig.videoUrlWebm && (
                <source src={videoConfig.videoUrlWebm} type="video/webm" />
              )}
              {videoConfig.videoUrlMp4 && (
                <source src={videoConfig.videoUrlMp4} type="video/mp4" />
              )}
              {/* Fallback for browsers that don't support video */}
              <img
                src={displayImageUrl}
                alt="The Pearl at Grand Harbour - Luxury lagoon community"
                className="w-full h-full object-cover"
              />
            </video>
          ) : (
            // Static Image Background
            <img
              src={displayImageUrl}
              alt="The Pearl at Grand Harbour - Luxury lagoon community"
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to default if configured image fails to load
                if (displayImageUrl !== DEFAULT_HERO_IMAGE) {
                  (e.target as HTMLImageElement).src = DEFAULT_HERO_IMAGE;
                }
              }}
            />
          )}
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 flex items-center justify-center">
        {/* Glass Effect Wrapper - Centered and fits content */}
        <div className="backdrop-blur-sm bg-black/20 rounded-3xl p-5 sm:p-6 lg:p-7 border border-white/10 shadow-2xl w-fit mx-auto">
          {showVIPPreviewCTA ? (
            /* Presales Lander - Large Centered Logo */
            <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in">
            {/* Large Centered Logo */}
            <div className="mb-12 animate-fade-in-up">
              <img
                src="/logo-wh-x-gh.png"
                alt="The Pearl at Grand Harbour"
                className="w-full max-w-6xl mx-auto drop-shadow-2xl object-contain"
              />
            </div>

            {/* VIP Preview CTA Button */}
            <div className="flex flex-col items-center gap-4 mb-10 animate-fade-in-up [animation-delay:0.2s]">
              <Button
                size="lg"
                variant="luxury"
                className="text-base px-10 py-7 shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1 bg-brand-teal hover:bg-brand-navy text-white font-semibold text-lg"
                onClick={onVIPPreviewClick || (() => console.warn('VIP Preview click handler not provided'))}
              >
                <Lock className="mr-2 h-5 w-5" />
                VIP Preview Access
              </Button>
            </div>

            {/* Hero Text Below Button */}
            <div className="max-w-3xl text-center animate-fade-in-up [animation-delay:0.4s]">
              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-semibold text-white mb-2 leading-tight drop-shadow-lg">
                Claim Your Place in the Heart of It All.
              </h1>
              <p className="font-display text-xl sm:text-2xl lg:text-3xl font-medium text-white/90 mb-6 drop-shadow-md flex items-center justify-center gap-2">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-brand-teal flex-shrink-0" />
                Grand Cayman, Cayman Islands
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl text-white/95 mb-10 font-normal max-w-2xl mx-auto drop-shadow-md">
                Welcome to the luxury residences at Grand Harbour. This is more than an address; it's a key to a vibrant community where everything you desire is moments from your front door. This is the life you've earned.
              </p>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center justify-center gap-6 text-white/85 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-brand-teal shadow-sm" />
                  <span className="font-medium">Grand Harbour Location</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-brand-teal shadow-sm" />
                  <span className="font-medium">~74 Residences</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-brand-teal shadow-sm" />
                  <span className="font-medium">Signature Lagoon</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-brand-teal shadow-sm" />
                  <span className="font-medium">Cayman Islands</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Regular Hero Content - Centered */
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-semibold text-white mb-2 leading-tight drop-shadow-lg">
              Claim Your Place in the Heart of It All.
            </h1>
            <p className="font-display text-xl sm:text-2xl lg:text-3xl font-medium text-white/90 mb-6 drop-shadow-md flex items-center justify-center gap-2">
              <MapPin className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-brand-teal flex-shrink-0" />
              Grand Cayman, Cayman Islands
            </p>
            <p className="text-lg sm:text-xl lg:text-2xl text-white/95 mb-10 font-normal max-w-2xl mx-auto drop-shadow-md">
              Welcome to the luxury residences at Grand Harbour. This is more than an address; it's a key to a vibrant community where everything you desire is moments from your front door. This is the life you've earned.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12 justify-center">
              <Button
                size="lg"
                variant="luxury"
                className="text-base px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                onClick={() => {
                  trackEvent('cta_click', { location: 'hero', action: 'register' });
                  onRegisterClick();
                }}
              >
                Register Interest
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-base px-8 py-6 border-2 border-white text-white hover:bg-brand-teal hover:text-white hover:border-brand-teal transition-all duration-300 transform hover:-translate-y-1 backdrop-blur-sm bg-white/10"
                onClick={() => {
                  trackEvent('cta_click', { location: 'hero', action: 'book_consult' });
                  onConsultClick();
                }}
              >
                Book 15-min Consult
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-white/85 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-brand-teal shadow-sm" />
                <span className="font-medium">Grand Harbour Location</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-brand-teal shadow-sm" />
                <span className="font-medium">~74 Residences</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-brand-teal shadow-sm" />
                <span className="font-medium">Signature Lagoon</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-brand-teal shadow-sm" />
                <span className="font-medium">Cayman Islands</span>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Scroll Indicator - Hide when VIP preview CTA is shown */}
      {!showVIPPreviewCTA && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce z-10">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/70 rounded-full" />
          </div>
        </div>
      )}
    </section>
  );
});

Hero.displayName = 'Hero';
